import React from "react";
import {
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
  FormControlLabelProps,
  MenuItemProps,
  FormGroup,
  FormGroupProps,
  Checkbox,
  CheckboxProps,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  TextFieldProps,
  Autocomplete,
  AutocompleteProps,
  Chip,
  Box,
} from "@mui/material";
import {
  Controller,
  UseControllerProps,
  useFormContext,
  ValidationRule,
} from "react-hook-form";
import { SelectProps } from "@mui/material/Select/Select";
import { Cancel } from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers";

type MUITextFieldWithRHFController = UseControllerProps &
  Partial<TextFieldProps>;

type TextFieldRHFProps = MUITextFieldWithRHFController & {
  textFieldLabel: string;
};

/**
 * Since we are using react hook form for validation we cannot use the "required" property on the HTML input element. This means the little red star will not show up in the browser indicating to the user that the field is required. so instead we will manually put the star in the label.
 * @param label - The label text
 * @param rules - The validation rules passed into the parameter
 * @returns The label value which should be used
 */
function putRequiredInLabel(
  label: string,
  rules?: { required?: string | boolean | ValidationRule<boolean> }
): JSX.Element {
  if (rules && rules.required) {
    return (
      <>
        {label}
        <span style={{ color: "red" }}> *</span>
      </>
    );
  }

  return <>{label}</>;
}

/**
 * Converts a simple array of values to an array of menu item options.
 * @param arr - Array of values
 * @returns Menu item options, using the array values as value fields.
 */
export function arrToMenuItemOptions(arr: string[]): menuItemOption[] {
  return arr.map((option) => {
    return {
      value: option,
    };
  });
}

export function TextFieldRHF({
  rules,
  name,
  textFieldLabel,
  ...rest
}: TextFieldRHFProps) {
  const { control } = useFormContext();
  return (
    <Controller
      name={name}
      rules={rules}
      control={control}
      render={({ field, fieldState: { error } }) => {
        return (
          <TextField
            label={putRequiredInLabel(textFieldLabel || name, rules)}
            error={!!error}
            helperText={error ? error.message : undefined}
            {...field}
            {...rest}
          />
        );
      }}
    />
  );
}

interface RadioGroupOption
  extends Pick<FormControlLabelProps, "label" | "value"> {
  extra?: React.ReactNode;
}

type RadioGroupRHFProps = UseControllerProps &
  FormGroupProps & {
    radioGroupOptions: RadioGroupOption[];
    groupLabel: string;
    footerText?: string;
  };

export function RadioGroupRHF({
  name,
  rules,
  radioGroupOptions,
  groupLabel,
  footerText,
  ...rest
}: RadioGroupRHFProps) {
  const { control } = useFormContext();
  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field, fieldState: { error } }) => {
        return (
          <FormControl error={!!error}>
            <FormLabel id={field.name}>
              {putRequiredInLabel(groupLabel, rules)}
            </FormLabel>
            <RadioGroup
              aria-labelledby={field.name}
              {...field}
              sx={{ paddingLeft: 1 }}
              {...rest}
            >
              {radioGroupOptions.map((props: RadioGroupOption) => (
                <React.Fragment key={`key-${props.value}`}>
                  <FormControlLabel {...props} control={<Radio />} />
                  {props.extra}
                </React.Fragment>
              ))}
            </RadioGroup>
            {error && (
              <FormHelperText error={true}>{error.message}</FormHelperText>
            )}
            {footerText && <FormHelperText>{footerText}</FormHelperText>}
          </FormControl>
        );
      }}
    />
  );
}

interface menuItemOption extends MenuItemProps {
  label?: string;
}

type MUISelectFieldWithRHFController = UseControllerProps &
  Partial<SelectProps>;

type SelectRHFProps = MUISelectFieldWithRHFController & {
  menuItemOptions: menuItemOption[];
  selectLabel: string;
  footerText?: string;

  /**
   * If true then more than one option can be selected at a time.
   */
  multiple?: boolean;
};

export function SelectRHF({
  name,
  rules,
  menuItemOptions,
  selectLabel,
  footerText,
  multiple,
  ...rest
}: SelectRHFProps) {
  const { control, setValue, getValues } = useFormContext();
  const labelId = `${selectLabel}-select-label`;

  const generateHandleOnDelete = (value: string) => {
    return () => {
      setValue(
        name,
        getValues(name).filter((v: string) => v !== value)
      );
    };
  };

  const renderValue =
    multiple !== true
      ? undefined
      : (selected: string[]) => (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
            }}
          >
            {selected.map((value: string) => (
              <Box
                key={`select-value-${value}`}
                sx={{
                  display: "inline",
                  p: 0.5,
                }}
              >
                <Chip
                  variant="outlined"
                  label={value}
                  onDelete={generateHandleOnDelete(value)}
                  deleteIcon={
                    <Cancel onMouseDown={(e) => e.stopPropagation()} />
                  }
                />
              </Box>
            ))}
          </Box>
        );

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field, fieldState: { error } }) => {
        return (
          <FormControl error={!!error}>
            <InputLabel id={labelId}>
              {putRequiredInLabel(selectLabel, rules)}
            </InputLabel>
            <Select
              labelId={labelId}
              id={`${selectLabel}-status-select`}
              label={putRequiredInLabel(selectLabel, rules)}
              multiple={multiple}
              renderValue={renderValue}
              MenuProps={{
                style: {
                  maxHeight: 400,
                },
              }}
              {...field}
              {...rest}
            >
              {menuItemOptions.map(({ label, value }) => (
                <MenuItem key={`${value}`} value={value}>
                  {label || value}
                </MenuItem>
              ))}
            </Select>
            {error && (
              <FormHelperText error={true}>{error.message}</FormHelperText>
            )}
            {footerText && <FormHelperText>{footerText}</FormHelperText>}
          </FormControl>
        );
      }}
    />
  );
}

type MUIAutocompleteFieldWithRHFController = UseControllerProps &
  Omit<
    AutocompleteProps<
      string,
      boolean | undefined,
      boolean | undefined,
      boolean | undefined
    >,
    "renderInput"
  >;

type AutocompleteRHFProps = MUIAutocompleteFieldWithRHFController & {
  selectLabel: string;
};

export function AutocompleteRHF({
  name,
  rules,
  selectLabel,
  ...rest
}: AutocompleteRHFProps) {
  const { control } = useFormContext();

  return (
    <Controller
      control={control}
      name={name}
      rules={rules}
      render={({ field: { onChange, value }, fieldState: { error } }) => (
        <FormControl error={!!error}>
          <Autocomplete
            {...rest}
            ChipProps={{
              variant: "outlined",
            }}
            renderInput={(params) => {
              return (
                <TextField
                  {...params}
                  label={putRequiredInLabel(selectLabel, rules)}
                  error={!!error}
                />
              );
            }}
            onChange={(event, values, reason) => onChange(values)}
            value={value}
          />
          {error && (
            <FormHelperText error={true}>{error.message}</FormHelperText>
          )}
        </FormControl>
      )}
    />
  );
}

interface CheckboxOptions extends CheckboxProps {
  label?: string;
  name: string;
}

interface CheckboxGroupRHFProps extends UseControllerProps {
  checkboxOptions: CheckboxOptions[];
  groupLabel: string;
  footerText?: string;
}

export function CheckboxGroupRHF({
  name,
  checkboxOptions,
  groupLabel,
  footerText,
}: CheckboxGroupRHFProps) {
  return (
    <FormControl component="fieldset" variant="standard">
      <FormLabel component="legend">{groupLabel}</FormLabel>
      <FormGroup>
        {checkboxOptions.map((checkboxOption) => {
          return (
            <CheckboxRHF
              key={`${name}.${checkboxOption.name}`}
              name={`${name}.${checkboxOption.name}`}
              label={checkboxOption.name}
            />
          );
        })}
        {footerText && (
          <FormHelperText sx={{ ml: 1 }}>{footerText}</FormHelperText>
        )}
      </FormGroup>
    </FormControl>
  );
}

interface CheckboxRHFProps extends UseControllerProps {
  label?: string;
  defaultValue?: boolean;
}

export function CheckboxRHF({
  name,
  label,
  defaultValue = false,
}: CheckboxRHFProps) {
  const { control } = useFormContext();
  return (
    <FormControlLabel
      key={`${name}`}
      control={
        <Controller
          name={`${name}`}
          control={control}
          defaultValue={defaultValue}
          render={({ field }) => {
            return (
              <Checkbox
                {...field}
                name={`${name}`}
                key={`${name}`}
                sx={{ pl: 0 }}
                checked={field.value}
              />
            );
          }}
        />
      }
      label={label || name}
      sx={{ mr: 0, ml: 1 }}
    />
  );
}

export function ConditionalRHFRender({
  dependantField,
  dependantValue,
  children,
}: {
  dependantField: string;
  dependantValue: string | boolean;
  children: React.ReactNode;
}) {
  const { watch } = useFormContext();
  const watchedValue = watch(dependantField);
  if (watchedValue === dependantValue) {
    return <>{children}</>;
  }
  return null;
}

type DatePickerProps = UseControllerProps & {
  name: string;
  textFieldLabel: string;
};

/**
 * This component requires React Context provided by LocalizationProvider from @mui/x-date-pickers.
 * @example
 * ```tsx
 * import { LocalizationProvider } from "@mui/x-date-pickers";
 * import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
 * <LocalizationProvider dateAdapter={AdapterDateFns}>
 *   <DatePickerRHF />
 * </LocalizationProvider>
 * ```
 */
export function DatePickerRHF({
  name,
  textFieldLabel,
  rules,
}: DatePickerProps) {
  const { control } = useFormContext();

  return (
    <Controller
      control={control}
      name={name}
      rules={rules}
      render={({ field: { onChange, value }, fieldState: { error } }) => (
        <FormControl error={!!error}>
          <DatePicker
            value={
              value.length === 0
                ? null
                : value /* If value is empty and passed to the data picker then the date picker will say it is invalid, so we don't tell date picker about the value until the user has set it. */
            }
            onChange={(value) => {
              if (value === null) {
                // RHF doesn't accept null values
                onChange("");
              } else {
                onChange(value);
              }
            }}
            renderInput={(props) => (
              <TextField
                {...props}
                label={putRequiredInLabel(textFieldLabel, rules)}
                error={!!error}
              />
            )}
          />
          {error && (
            <FormHelperText error={true}>{error.message}</FormHelperText>
          )}
        </FormControl>
      )}
    />
  );
}
